import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { writeUserNotification } from "./userNotifications";

// ── Create ─────────────────────────────────────────────────────────────────

export async function createCall({ programTag, title, pollBroadcastId, createdBy, targetType = "program", targetUserIds = [] }) {
  const ref = await addDoc(collection(db, "calls"), {
    programTag,
    title: title || (targetType === "users" && targetUserIds.length === 1 ? "Call" : "Group Call"),
    status: "polling",
    pollBroadcastId: pollBroadcastId || null,
    winnerOption: null,
    scheduledTime: null,
    roomUrl: null,
    roomName: null,
    targetType,
    targetUserIds,
    createdBy,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function createDirectCall({ programTag, title, targetType, targetUserIds, scheduledTime, createdBy }) {
  const ref = await addDoc(collection(db, "calls"), {
    programTag: programTag || "",
    title: title || "Call",
    status: "scheduled",
    pollBroadcastId: null,
    winnerOption: null,
    scheduledTime: scheduledTime ? Timestamp.fromDate(new Date(scheduledTime)) : Timestamp.fromDate(new Date()),
    roomUrl: null,
    roomName: null,
    targetType: targetType || "program",
    targetUserIds: targetUserIds || [],
    createdBy,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ── Read ───────────────────────────────────────────────────────────────────

export async function getUpcomingCall(programTag) {
  if (!programTag) return null;
  const snap = await getDocs(
    query(
      collection(db, "calls"),
      where("programTag", "==", programTag),
      where("status", "in", ["polling", "scheduled"]),
      orderBy("createdAt", "desc"),
      limit(1)
    )
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export function subscribeToCall(programTag, userId, callback) {
  if (!programTag) return () => {};
  return onSnapshot(
    query(collection(db, "calls"), where("programTag", "==", programTag), limit(20)),
    (snap) => {
      if (snap.empty) { callback(null); return; }
      const calls = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) =>
          (c.status === "polling" || c.status === "scheduled") &&
          (c.targetType !== "users" || (c.targetUserIds || []).includes(userId))
        )
        .sort((a, b) => (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0));
      callback(calls[0] || null);
    },
    () => callback(null)
  );
}

export function subscribeToAdminCall(adminUid, callback) {
  if (!adminUid) return () => {};
  return onSnapshot(
    query(collection(db, "calls"), where("createdBy", "==", adminUid)),
    (snap) => {
      const calls = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => c.status === "polling" || c.status === "scheduled")
        .sort((a, b) => (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0));
      callback(calls[0] || null);
    },
    () => callback(null)
  );
}

export function subscribeToDirectCall(userId, callback) {
  if (!userId) return () => {};
  return onSnapshot(
    query(collection(db, "calls"), where("targetUserIds", "array-contains", userId)),
    (snap) => {
      const calls = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => c.status === "polling" || c.status === "scheduled")
        .sort((a, b) => (b.createdAt?.toDate?.()?.getTime?.() || 0) - (a.createdAt?.toDate?.()?.getTime?.() || 0));
      callback(calls[0] || null);
    },
    () => callback(null)
  );
}

export async function getPastCalls(programTag, adminUid, userId = null) {
  const queries = [];
  if (programTag) queries.push(getDocs(query(collection(db, "calls"), where("programTag", "==", programTag), limit(50))));
  if (adminUid) queries.push(getDocs(query(collection(db, "calls"), where("createdBy", "==", adminUid), limit(50))));
  if (userId) queries.push(getDocs(query(collection(db, "calls"), where("targetUserIds", "array-contains", userId), limit(50))));
  if (queries.length === 0) return [];
  const snaps = await Promise.all(queries);
  const seen = new Set();
  const all = [];
  for (const snap of snaps) {
    for (const d of snap.docs) {
      if (!seen.has(d.id)) { seen.add(d.id); all.push({ id: d.id, ...d.data() }); }
    }
  }
  return all
    .filter((c) => c.status === "ended")
    .sort((a, b) => (b.scheduledTime?.toDate?.()?.getTime?.() || 0) - (a.scheduledTime?.toDate?.()?.getTime?.() || 0))
    .slice(0, 10);
}

// ── Finalize ───────────────────────────────────────────────────────────────

export async function finalizeCall(callId, { scheduledTime, roomUrl, roomName, winnerOption, scheduledEndTime }) {
  await updateDoc(doc(db, "calls", callId), {
    status: "scheduled",
    scheduledTime: Timestamp.fromDate(new Date(scheduledTime)),
    roomUrl: roomUrl || null,
    roomName: roomName || null,
    winnerOption: winnerOption || null,
    scheduledEndTime: scheduledEndTime ? Timestamp.fromDate(new Date(scheduledEndTime)) : null,
  });
}

export async function updateCallSchedule(callId, { scheduledTime, scheduledEndTime }) {
  const data = {};
  if (scheduledTime) data.scheduledTime = Timestamp.fromDate(new Date(scheduledTime));
  data.scheduledEndTime = scheduledEndTime ? Timestamp.fromDate(new Date(scheduledEndTime)) : null;
  await updateDoc(doc(db, "calls", callId), data);
}

export async function endCall(callId) {
  await updateDoc(doc(db, "calls", callId), { status: "ended" });
}

export async function updateCallRecordingUrl(callId, recordingUrl) {
  await updateDoc(doc(db, "calls", callId), { recordingUrl: recordingUrl || null });
}

export async function deleteCall(callId) {
  await deleteDoc(doc(db, "calls", callId));
}

// ── Auto-finalize (called after every call_poll response) ──────────────────

export async function checkAndAutoFinalize({ broadcastId, callId }) {
  if (!broadcastId || !callId) return;

  // Fetch broadcast to get targeting and creator
  const broadcastSnap = await getDoc(doc(db, "broadcasts", broadcastId));
  if (!broadcastSnap.exists()) return;
  const broadcast = broadcastSnap.data();
  const programTag = broadcast.programTag || "";
  const targetType = broadcast.targetType || "program";
  const broadcastTargetUserIds = broadcast.targetUserIds || [];
  const createdBy = broadcast.createdBy || "";

  // Determine the exact set of users who should vote
  let eligibleUserIds = [];
  if (targetType === "users" && broadcastTargetUserIds.length > 0) {
    eligibleUserIds = broadcastTargetUserIds;
  } else if (programTag) {
    const usersSnap = await getDocs(
      query(collection(db, "users"), where("programTag", "==", programTag), where("status", "==", "active"))
    );
    eligibleUserIds = usersSnap.docs.map((d) => d.id);
  }
  if (eligibleUserIds.length === 0) return;

  // Get only responses from eligible voters
  const responsesSnap = await getDocs(
    query(collection(db, "broadcastResponses"), where("broadcastId", "==", broadcastId))
  );
  const eligibleResponses = responsesSnap.docs
    .map((d) => d.data())
    .filter((r) => eligibleUserIds.includes(r.userId));

  if (eligibleResponses.length < eligibleUserIds.length) return; // waiting on more votes

  // Tally — users can pick multiple times they're available
  const tally = {};
  for (const r of eligibleResponses) {
    for (const sel of r.selections || []) {
      tally[sel] = (tally[sel] || 0) + 1;
    }
  }
  if (Object.keys(tally).length === 0) return;

  const maxVotes = Math.max(...Object.values(tally));
  const topOptions = Object.entries(tally)
    .filter(([, count]) => count === maxVotes)
    .map(([opt]) => opt);

  // Determine the winner
  let winner;
  if (topOptions.length === 1) {
    // Clear winner
    winner = topOptions[0];
  } else if (eligibleUserIds.length === 1) {
    // Single voter marked multiple times as available — auto-pick the first option
    // from the broadcast's ordered options list so admin's ordering is honored
    const broadcastOptions = broadcast.options || [];
    winner = broadcastOptions.find((opt) => topOptions.includes(opt)) || topOptions[0];
  } else {
    // Genuine multi-voter tie — only then does admin need to break it
    if (createdBy) {
      const tiedTimes = topOptions.map((t) => formatCallTime(t)).join(", ");
      writeUserNotification({
        toUserId: createdBy,
        type: "call_tie",
        message: "It's a tie! Please choose a time for the group call. Tied: " + tiedTimes,
        fromName: "Poll Result",
        fromAvatar: "",
        postId: callId,
        screen: "admin_broadcasts",
      });
    }
    return;
  }

  // Create Daily.co room and finalize
  let roomUrl = null, roomName = null;
  try {
    const room = await createDailyRoom(callId, winner);
    roomUrl = room.roomUrl;
    roomName = room.roomName;
  } catch (_) {}

  await finalizeCall(callId, { scheduledTime: winner, winnerOption: winner, roomUrl, roomName });

  // Notify all eligible users using the call's targeting info
  const callSnap = await getDoc(doc(db, "calls", callId));
  if (callSnap.exists()) {
    await notifyCallParticipants({ id: callId, ...callSnap.data() }, winner);
  }
}

// ── Daily.co room creation ─────────────────────────────────────────────────

export async function deleteDailyRoom(roomName) {
  if (!roomName) return;
  const configSnap = await getDoc(doc(db, "appConfig", "dailyCo"));
  const apiKey = configSnap.data()?.apiKey;
  if (!apiKey) return;
  await fetch("https://api.daily.co/v1/rooms/" + roomName, {
    method: "DELETE",
    headers: { Authorization: "Bearer " + apiKey },
  });
}

export async function createDailyRoom(callId, scheduledTime, { screenshareMode = "everyone" } = {}) {
  const configSnap = await getDoc(doc(db, "appConfig", "dailyCo"));
  const apiKey = configSnap.data()?.apiKey;
  if (!apiKey) throw new Error("Daily.co API key not configured. Go to Admin → Calls to add it.");

  const roomName = `call-${callId}`;

  // Delete any existing room first to avoid name-conflict errors
  await fetch(`https://api.daily.co/v1/rooms/${roomName}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${apiKey}` },
  }).catch(() => {});

  const scheduledDate = new Date(scheduledTime);
  const expBase = Math.max(scheduledDate.getTime(), Date.now());
  const exp = Math.floor(expBase / 1000) + 7200;

  const res = await fetch("https://api.daily.co/v1/rooms", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: roomName,
      privacy: "public",
      properties: {
        enable_screenshare: screenshareMode !== "none",
        ...(screenshareMode === "admin" ? { screenshare_send_level: "admin" } : {}),
        enable_chat: true,
        enable_prejoin_ui: false,
        enable_recording: "cloud",
        exp,
      },
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Daily.co API error (${res.status})`);
  }
  const data = await res.json();
  return { roomUrl: data.url, roomName: data.name };
}

// ── Notify participants (respects targetType/targetUserIds on the call) ───────

export async function notifyCallParticipants(call, scheduledTime) {
  if (!call?.id) return;
  const timeLabel = formatCallTime(scheduledTime);
  let usersToNotify = [];
  if (call.targetType === "users" && (call.targetUserIds || []).length > 0) {
    const userDocs = await Promise.all(call.targetUserIds.map((uid) => getDoc(doc(db, "users", uid))));
    usersToNotify = userDocs.filter((d) => d.exists()).map((d) => ({ id: d.id, ...d.data() }));
  } else if (call.programTag) {
    const snap = await getDocs(
      query(collection(db, "users"), where("programTag", "==", call.programTag), where("status", "==", "active"))
    );
    usersToNotify = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  await Promise.all(
    usersToNotify.map((u) =>
      writeUserNotification({
        toUserId: u.id,
        type: "call_scheduled",
        message: (call.targetType === "users" && (call.targetUserIds || []).length === 1 ? "Call" : "Group call") + " confirmed: " + timeLabel + ". Tap to join in the Calls tab.",
        fromName: "Your Coach",
        fromAvatar: "",
        postId: call.id,
        screen: "calls",
      })
    )
  );
}

// Keep for backward compatibility
export async function notifyGroupOfCall(programTag, callId, scheduledTime) {
  await notifyCallParticipants({ id: callId, programTag, targetType: "program" }, scheduledTime);
}

// ── Helpers ────────────────────────────────────────────────────────────────

export function formatCallTime(isoOrTimestamp) {
  if (!isoOrTimestamp) return "";
  const d = isoOrTimestamp?.toDate ? isoOrTimestamp.toDate() : new Date(isoOrTimestamp);
  if (isNaN(d.getTime())) return String(isoOrTimestamp);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) +
    " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export function getCallStatus(call) {
  if (!call) return null;
  if (call.status === "ended") return "ended";
  if (call.status === "polling") return "polling";
  if (!call.scheduledTime) return "scheduled";
  const t = call.scheduledTime?.toDate ? call.scheduledTime.toDate() : new Date(call.scheduledTime);
  const now = new Date();
  const endTime = call.scheduledEndTime
    ? (call.scheduledEndTime?.toDate ? call.scheduledEndTime.toDate() : new Date(call.scheduledEndTime))
    : new Date(t.getTime() + 3 * 60 * 60 * 1000);
  const fiveMinsEarly = new Date(t.getTime() - 5 * 60 * 1000);
  if (now >= fiveMinsEarly && now <= endTime) return "live";
  if (now > endTime) return "ended";
  return "scheduled";
}
